/**
 * "Pin for all": a host or co-host pins one tile on everybody's screen.
 *
 * The tile menu offered the item and wired it to the same handler as "Pin for
 * me", so it only ever toggled the sender's own pin — and on a tile already
 * pinned for yourself it UNPINNED it. Nothing was sent; nobody else's screen
 * changed.
 *
 * This is the decision half, as `layout-sync` is for layouts: what to send and
 * what to believe. It rides the generic `broadcast` event for the same reason
 * (a bespoke event an older server does not implement would take the sender's
 * channel down).
 *
 * ## Who may move everyone's screen
 *
 * Only a moderator — and that is checked on RECEIPT, against the sender the
 * server stamped on the broadcast (`from`), not against anything in the body.
 * The menu only offers the item to moderators, but a hidden menu item is not a
 * permission: any participant could send the envelope by hand.
 *
 * ## Someone who joins after the pin
 *
 * A broadcast reaches only who is connected when it is sent, so a late joiner
 * (or anyone who refreshed) never heard the pin and sat in the ordinary grid
 * while the rest of the room was pinned. On joining, a client asks
 * (`query`); every moderator holding a pin for all answers with the pin,
 * addressed to the asker alone (`to`), so nobody already in the room is
 * re-pinned — or un-done from whatever they chose since. Anyone may ask; only
 * a moderator's answer is believed, as for any pin.
 */

export type PinSyncEnvelope = {
  __inmeet?: unknown
  action?: unknown
  participantId?: unknown
  to?: unknown
}

export type PinSyncCommand =
  | { action: 'set'; participantId: string }
  | { action: 'clear' }
  /** Someone joined and asks whether a pin for all is in force. */
  | { action: 'query'; from: string }

/** A pin for everyone, or with `to` for that one participant (the answer to a query). */
export function pinForAllBody(participantId: string, to?: string) {
  return to ? { __inmeet: 'pin', action: 'set', participantId, to } : { __inmeet: 'pin', action: 'set', participantId }
}

export function pinQueryBody() {
  return { __inmeet: 'pin', action: 'query' }
}

export function unpinForAllBody() {
  return { __inmeet: 'pin', action: 'clear' }
}

/**
 * The command an incoming broadcast carries, or null to ignore it.
 *
 * Own messages are dropped: the sender already applied the pin before sending,
 * and every client receives its own broadcasts.
 */
export function readPinSync(
  payload: unknown,
  selfId: string | null | undefined,
  isModerator: (peerId: string) => boolean,
): PinSyncCommand | null {
  const from = (payload as { from?: unknown })?.from
  const env = ((payload as { body?: PinSyncEnvelope })?.body ?? {}) as PinSyncEnvelope
  if (env.__inmeet !== 'pin') return null
  if (typeof from !== 'string' || !from) return null
  if (selfId && from === selfId) return null
  if (env.action === 'query') return { action: 'query', from }
  if (!isModerator(from)) return null
  if (typeof env.to === 'string' && env.to !== selfId) return null
  if (env.action === 'clear') return { action: 'clear' }
  if (env.action === 'set' && typeof env.participantId === 'string' && env.participantId) {
    return { action: 'set', participantId: env.participantId }
  }
  return null
}
